import {
  Code,
  Download,
  Eye,
  FileText,
  PanelRightClose,
  Save,
  X,
} from "lucide-react";
import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/api/client";

const MonacoEditor = lazy(() => import("@monaco-editor/react"));

const CODE_EXTS: Record<string, string> = {
  ts: "typescript",
  tsx: "typescript",
  js: "javascript",
  jsx: "javascript",
  py: "python",
  sh: "shell",
  bash: "shell",
  json: "json",
  jsonl: "json",
  yaml: "yaml",
  yml: "yaml",
  toml: "toml",
  xml: "xml",
  html: "html",
  css: "css",
  scss: "scss",
  sql: "sql",
  md: "markdown",
  dockerfile: "dockerfile",
  makefile: "makefile",
  rs: "rust",
  go: "go",
  java: "java",
  c: "c",
  cpp: "cpp",
  h: "c",
  rb: "ruby",
  php: "php",
};

const TEXT_EXTS = new Set([
  ...Object.keys(CODE_EXTS),
  "txt",
  "log",
  "csv",
  "env",
  "cfg",
  "ini",
  "conf",
  "gitignore",
  "lock",
  "editorconfig",
]);

const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "svg", "webp"]);
const PDF_EXTS = new Set(["pdf"]);
const OFFICE_EXTS = new Set(["docx", "xlsx", "xls", "pptx", "doc"]);

interface FileViewerProps {
  deskId: string;
  filePath: string;
  fileName: string;
  root: "data" | "sprite";
  onClose: () => void;
  onCollapseSibling?: () => void;
}

export function FileViewer({
  deskId,
  filePath,
  fileName,
  root,
  onClose,
  onCollapseSibling,
}: FileViewerProps) {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  const isText = TEXT_EXTS.has(ext) || fileName.startsWith(".");
  const isMarkdown = ext === "md";
  const isImage = IMAGE_EXTS.has(ext);
  const isPdf = PDF_EXTS.has(ext);
  const isOffice = OFFICE_EXTS.has(ext);
  const monacoLang = CODE_EXTS[ext] ?? (isText ? "plaintext" : null);

  const [content, setContent] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"saved" | "error" | null>(null);
  const [mdPreview, setMdPreview] = useState(true);

  const hasUnsavedChanges = editedContent !== null && editedContent !== content;

  useEffect(() => {
    setContent(null);
    setEditedContent(null);
    setError(null);
    setSaveStatus(null);
    setMdPreview(ext === "md");
    if (!isText) return;
    setLoading(true);
    api
      .readFile(deskId, filePath, root)
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [deskId, filePath, root, isText, ext]);

  const handleSave = useCallback(async () => {
    if (editedContent === null || editedContent === content) return;
    setSaving(true);
    setSaveStatus(null);
    try {
      const dir = filePath.split("/").slice(0, -1).join("/") || "/";
      const blob = new File([editedContent], fileName, { type: "text/plain" });
      await api.uploadFile(deskId, dir, blob);
      setContent(editedContent);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 2000);
    } catch {
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  }, [editedContent, content, deskId, filePath, fileName]);

  const downloadUrl = api.downloadFileUrl(deskId, filePath, root);

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: "var(--white)", minWidth: 0 }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-2 px-3 py-1.5 border-b"
        style={{
          borderColor: "var(--blt)",
          background: "var(--surface)",
          flexShrink: 0,
        }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <FileText
            size={12}
            className="shrink-0"
            style={{ color: "var(--t3)" }}
          />
          <span
            className="text-[12px] truncate"
            style={{
              fontFamily: "var(--font-mono)",
              color: "var(--t1)",
            }}
          >
            {fileName}
          </span>
          <span
            className="text-[10px] uppercase tabular-nums"
            style={{
              fontFamily: "var(--font-mono)",
              color: "var(--t4)",
              letterSpacing: "0.06em",
            }}
          >
            {ext}
          </span>
          {hasUnsavedChanges && (
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: "var(--warm)" }}
              title="Unsaved changes"
            />
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {isMarkdown && content !== null && (
            <button
              type="button"
              onClick={() => setMdPreview(!mdPreview)}
              className="p-1.5 rounded"
              style={{
                background: mdPreview
                  ? "color-mix(in srgb, var(--accent) 10%, transparent)"
                  : "transparent",
                color: mdPreview ? "var(--accent)" : "var(--t3)",
              }}
              title={mdPreview ? "Show code" : "Preview markdown"}
            >
              {mdPreview ? <Code size={12} /> : <Eye size={12} />}
            </button>
          )}
          {hasUnsavedChanges && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded"
              style={{
                background: "var(--accent)",
                color: "var(--white)",
                opacity: saving ? 0.5 : 1,
              }}
            >
              <Save size={10} />
              {saving ? "Saving…" : "Save"}
            </button>
          )}
          {saveStatus === "saved" && (
            <span
              className="text-[10px] px-1.5"
              style={{ color: "var(--ok)" }}
            >
              Saved
            </span>
          )}
          {saveStatus === "error" && (
            <span
              className="text-[10px] px-1.5"
              style={{ color: "var(--err)" }}
            >
              Failed
            </span>
          )}
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded hover:bg-[var(--hover)]"
            style={{ color: "var(--t3)" }}
            title="Download"
          >
            <Download size={12} />
          </a>
          {onCollapseSibling && (
            <button
              type="button"
              onClick={onCollapseSibling}
              className="p-1.5 rounded hover:bg-[var(--hover)]"
              style={{ color: "var(--t3)" }}
              title="Collapse file tree"
            >
              <PanelRightClose size={12} />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-[var(--hover)]"
            style={{ color: "var(--t3)" }}
            title="Close file"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-hidden"
        style={{ background: "var(--white)", minHeight: 0 }}
      >
        {loading && (
          <div className="flex items-center justify-center h-full">
            <span
              className="w-5 h-5 rounded-full"
              style={{
                border: "2px solid var(--accent)",
                borderTopColor: "transparent",
                animation: "spin 0.8s linear infinite",
              }}
            />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center h-full gap-2 px-6 text-center">
            <p
              className="text-[13px]"
              style={{ color: "var(--err)" }}
            >
              Couldn't read file
            </p>
            <p
              className="text-[11px]"
              style={{
                color: "var(--t4)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {error}
            </p>
          </div>
        )}

        {isMarkdown && mdPreview && content !== null && !loading && (
          <div
            className="h-full overflow-auto px-6 py-5 prose-chat"
            style={{
              fontFamily: "var(--font-body)",
              color: "var(--t1)",
              fontSize: "14px",
              lineHeight: "1.65",
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {editedContent ?? content}
            </ReactMarkdown>
          </div>
        )}

        {isText &&
          monacoLang &&
          content !== null &&
          !loading &&
          !(isMarkdown && mdPreview) && (
            <Suspense
              fallback={
                <pre
                  className="p-4 text-xs whitespace-pre-wrap h-full overflow-auto"
                  style={{
                    fontFamily: "var(--font-mono)",
                    color: "var(--t2)",
                  }}
                >
                  {content}
                </pre>
              }
            >
              <MonacoEditor
                value={editedContent ?? content}
                language={monacoLang}
                theme="vs"
                onChange={(value) => setEditedContent(value ?? "")}
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                  lineHeight: 20,
                  fontFamily: "JetBrains Mono, ui-monospace, monospace",
                  renderLineHighlight: "gutter",
                  overviewRulerBorder: false,
                  hideCursorInOverviewRuler: true,
                  scrollbar: {
                    verticalScrollbarSize: 6,
                    horizontalScrollbarSize: 6,
                  },
                  padding: { top: 10, bottom: 10 },
                  wordWrap: "on",
                  lineNumbersMinChars: 3,
                  glyphMargin: false,
                  folding: true,
                  bracketPairColorization: { enabled: true },
                }}
                onMount={(editor) => {
                  editor.addAction({
                    id: "rondel-save",
                    label: "Save File",
                    keybindings: [2048 | 49],
                    run: () => handleSave(),
                  });
                }}
              />
            </Suspense>
          )}

        {isText && !monacoLang && content !== null && !loading && (
          <pre
            className="p-4 text-[13px] leading-relaxed whitespace-pre-wrap break-words overflow-auto h-full"
            style={{
              fontFamily: "var(--font-mono)",
              color: "var(--t2)",
            }}
          >
            {content}
          </pre>
        )}

        {isImage && (
          <div
            className="flex items-center justify-center h-full p-6"
            style={{ background: "var(--bg)" }}
          >
            <img
              src={downloadUrl}
              alt={fileName}
              className="max-w-full max-h-full object-contain rounded shadow"
            />
          </div>
        )}

        {isPdf && (
          <iframe
            src={downloadUrl}
            title={fileName}
            className="w-full h-full border-0"
          />
        )}

        {isOffice && (
          <OfficePlaceholder
            ext={ext}
            fileName={fileName}
            downloadUrl={downloadUrl}
          />
        )}

        {!isText && !isImage && !isPdf && !isOffice && !loading && (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-8">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "var(--surface)" }}
            >
              <FileText size={22} style={{ color: "var(--t3)" }} />
            </div>
            <p
              className="text-[13px]"
              style={{ color: "var(--t3)" }}
            >
              No preview for .{ext} files
            </p>
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium rounded"
              style={{
                background: "var(--accent)",
                color: "var(--white)",
              }}
            >
              <Download size={12} />
              Download
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function OfficePlaceholder({
  ext,
  fileName,
  downloadUrl,
}: {
  ext: string;
  fileName: string;
  downloadUrl: string;
}) {
  const label =
    ext === "docx"
      ? "Word Document"
      : ext === "xlsx" || ext === "xls"
        ? "Excel Spreadsheet"
        : ext === "pptx"
          ? "PowerPoint Presentation"
          : "Office Document";
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 px-8">
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center"
        style={{ background: "var(--surface)" }}
      >
        <FileText size={24} style={{ color: "var(--t3)" }} />
      </div>
      <div className="text-center">
        <p
          className="text-[13px] font-medium"
          style={{ color: "var(--t1)" }}
        >
          {fileName}
        </p>
        <p
          className="text-[11px] mt-1"
          style={{ color: "var(--t3)" }}
        >
          {label}
        </p>
      </div>
      <a
        href={downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium rounded"
        style={{ background: "var(--accent)", color: "var(--white)" }}
      >
        <Download size={12} />
        Download to open
      </a>
    </div>
  );
}
